import { i18n } from '@/plugins/i18n';
import bizAiService from '@/modules/bizai/services/BizAiService';
import { PULSE_ACTION_TYPE } from '@/modules/pulse/utils/pulseConstants';
import aiChatService from '../services/aiChatService';
import processRawText, { RECOMMENDATION_INTERACTION_TYPE, CALL_INTERACTION_TYPE } from '../utils/messageParser';
import { findFixtureByUserText, findReplyChunks } from '../utils/testModeFixtures';
import debugLogger from '../utils/debugLogger';
import { buildSelectedEntityPayload } from '../utils/entityUtils';
import { markSelectedRadioEntity } from '../utils/radioSelectionUtils';
import { MESSAGE_TRIGGERS, trackMessageSent } from '../utils/trackingUtils';

export const SESSION_STARTED_AT = Date.now();

let currentStreamController = null;
let currentStreamRequestId = 0;

const TEST_MODE_CHUNK_DELAY_MS = 40;

const buildUserMessage = (text, { isMarkdown = false } = {}) => ({
  role: 'user',
  text,
  isMarkdown,
  entityRefs: [],
  isStreaming: false,
});

const buildAiMessage = () => ({
  role: 'ai',
  rawText: '',
  reasoningSegments: [],
  text: '',
  suggestions: [],
  interactions: [],
  entities: [],
  entityRefs: [],
  streamStatus: 'thinking',
  isReasoning: false,
  isStreaming: true,
  isWaiting: true,
});

/** A finalized AI message authored on the client — parsed like a stream, no round-trip. */
const buildLocalAiMessage = (rawText) => ({
  ...buildAiMessage(),
  ...processRawText(rawText),
  rawText,
  streamStatus: null,
  isReasoning: false,
  isStreaming: false,
  isWaiting: false,
});

const withoutFalsy = (fields) => Object.fromEntries(Object.entries(fields).filter(([, v]) => v));

const STAMPED_INTERACTION_TYPES = [RECOMMENDATION_INTERACTION_TYPE, CALL_INTERACTION_TYPE];

const stampRecommendationFields = (message, recommendation) => ({
  ...message,
  interactions: message.interactions.map((interaction) => (
    STAMPED_INTERACTION_TYPES.includes(interaction.type)
      ? {
        ...interaction,
        ...withoutFalsy({
          uid: interaction.uid || recommendation.recommendation_uid,
          action_uid: interaction.action_uid || recommendation.uid,
          client_uid: recommendation.client_uid || interaction.client_uid,
          action: recommendation.action || interaction.action,
        }),
      }
      : interaction
  )),
});

/** Written for the assistant, not the user — English, not a locale key (see README). */
const buildExecutedRecommendationMessage = ({ actionUid, clientUid }) => (clientUid
  ? `The message to the client ${clientUid} has been sent successfully for the recommendation's action ${actionUid}`
  : `The message has been sent successfully for the recommendation's action ${actionUid}`);

export const RECOMMENDATION_BODY_MARKDOWN_FLAG = 'rollout.ai.recommendation_body_markdown';

const RECOMMENDATION_ENTITY_TYPE = 'ai_recommendation';

const buildRecommendationEntity = ({ recommendationUid, actionUid, action, result }) => ({
  type: RECOMMENDATION_ENTITY_TYPE,
  uid: recommendationUid,
  action_uid: actionUid,
  ...(action ? { action } : {}),
  ...(result ? { result } : {}),
});

const buildRecommendationTopic = ({ title, recommendationUid, actionUid, action, result }) => (
  recommendationUid
    ? { title: title || '', entity: buildRecommendationEntity({ recommendationUid, actionUid, action, result }) }
    : null);

const buildExecutedRecommendationTopic = (entity) => buildRecommendationTopic({
  ...entity,
  title: `Executed: ${entity.action || 'recommendation action'}`,
});

const extractResolveResult = (response) => {
  const { result } = response?.data?.data || response?.data || {};
  return result && typeof result === 'object' ? result : null;
};

// VCITA2-15185; derived, never restated (see the recommendation README, "Chat with BizAI").
export const CHAT_WITH_BIZAI_ACTION = PULSE_ACTION_TYPE.CHAT_WITH_BIZAI;

// Its CTA takes the user to the item's context instead of resolving the action,
// so it needs the same explicit branch `chat_with_bizai` has.
export const NAVIGATE_ACTION = PULSE_ACTION_TYPE.NAVIGATE;

const RECOMMENDATION_WORKFLOW_NAME = 'handle_ai_recommendation';

const buildRecommendationWorkflow = ({ recommendationUid, actionUid }) => ({
  name: RECOMMENDATION_WORKFLOW_NAME,
  entities: [buildRecommendationEntity({ recommendationUid, actionUid })],
});

/** Best-effort conversation_id when JSON.parse fails on SSE result (e.g. framing edge cases). */
const parseResultIdsFallback = (text) => {
  const mC = text.match(/"conversation_id"\s*:\s*"([0-9a-f-]{36})"/i);
  if (!mC?.[1]) return null;
  return { conversation_id: mC[1] };
};

const buildBackendTopic = (topic = {}) => ({
  ...(topic || {}),
  title: topic.title || '',
  toolTip: topic.tool_tip || topic.toolTip || null,
});

const extractLatestExchange = (messages = []) => {
  if (!messages.length) return { remainingMessages: [], exchangeMessages: [] };
  const lastMessage = messages[messages.length - 1];
  const previousMessage = messages[messages.length - 2];
  if (lastMessage?.role === 'ai' && ['user', 'topic-entity'].includes(previousMessage?.role)) {
    return {
      remainingMessages: messages.slice(0, -2),
      exchangeMessages: messages.slice(-2),
    };
  }
  return {
    remainingMessages: messages.slice(0, -1),
    exchangeMessages: messages.slice(-1),
  };
};

const lastTopicLastMessage = (topics) => {
  const last = topics[topics.length - 1];
  return last ? last.messages[last.messages.length - 1] : null;
};

/** Immutably updates the last message of the last topic. */
const withLastMessage = (state, updater) => {
  const topics = [...state.topics];
  const lastTopic = { ...topics[topics.length - 1] };
  const messages = [...lastTopic.messages];
  messages[messages.length - 1] = updater({ ...messages[messages.length - 1] });
  lastTopic.messages = messages;
  topics[topics.length - 1] = lastTopic;
  return topics;
};

/** A recommendation entity is a draft until the user actively engages with it. */
const isDraftRecommendation = (entity) => entity?.type === 'recommendation' && !!entity.draft;

/** True when a topic still holds an un-engaged (draft) recommendation entity. */
const topicHasDraftRecommendation = (topic) => (topic?.messages || [])
  .some((msg) => (msg?.entities || []).some(isDraftRecommendation));

/**
 * Clears the `draft` flag on recommendation entities, immutably.
 * With a `uid`, only the matching entity is promoted (acting on a specific card);
 * without one, the draft in the last topic is promoted (the chat target).
 */
const promoteRecommendationEntities = (topics, { uid } = {}) => {
  const lastIndex = topics.length - 1;
  return topics.map((topic, topicIndex) => {
    let changed = false;
    const messages = topic.messages.map((msg) => {
      if (!(msg?.entities || []).some(isDraftRecommendation)) return msg;
      let msgChanged = false;
      const entities = msg.entities.map((entity) => {
        if (!isDraftRecommendation(entity)) return entity;
        const matches = uid ? entity.uid === uid : topicIndex === lastIndex;
        if (!matches) return entity;
        msgChanged = true;
        return { ...entity, draft: false };
      });
      if (!msgChanged) return msg;
      changed = true;
      return { ...msg, entities };
    });
    return changed ? { ...topic, messages } : topic;
  });
};

const flagRecommendationEntities = (topics, { uid, key, value = true } = {}) => topics.map((topic) => {
  let changed = false;
  const messages = topic.messages.map((msg) => {
    if (!(msg?.entities || []).some((e) => e?.type === 'recommendation' && e.uid === uid)) return msg;
    changed = true;
    const entities = msg.entities.map((e) => (
      e?.type === 'recommendation' && e.uid === uid ? { ...e, [key]: value } : e
    ));
    return { ...msg, entities };
  });
  return changed ? { ...topic, messages } : topic;
});

/** Same SSE handling for send and confirm (both POST …/message/stream). */
const parseChatTopic = (text) => {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

const handleChatStreamEvent = (commit, { showStreamUi = true } = {}) => (event) => {
  if (showStreamUi) {
    commit('markFirstEventReceived');
    if (event.type === 'error') {
      commit('setError', (event.text && event.text.trim()) || 'Something went wrong. Please try again.');
    }
    if (event.type === 'delta_reasoning') commit('appendReasoningDelta', event.text);
    if (event.type === 'tool_status' || event.type === 'status') commit('appendStatus', event.text);
    if (event.type === 'text_delta') commit('appendTextDelta', event.text);
  }
  if (event.type === 'conversation_id') commit('setConversationId', event.text.trim());
  if (event.type === 'chat_topic') {
    const topic = parseChatTopic(event.text);
    if (topic?.title) commit('applyBackendChatTopic', topic);
  }
  if (event.type === 'result') {
    try {
      commit('setResult', JSON.parse(event.text));
    } catch {
      const partial = parseResultIdsFallback(event.text);
      if (partial) commit('setResult', partial);
    }
  }
};

const state = {
  panelOpen: false,
  panelSource: null,
  topics: [],
  conversationId: null,
  isStreaming: false,
  error: null,
  testMode: false,
  activeTopic: null,
  cancelledInputText: null,
  startRecordingRequestId: 0,
  lastBackendChatTopic: null,
  failedRecommendationSend: null,
  activeRecommendation: null,
};

const getters = {
  panelOpen: (s) => s.panelOpen,
  panelSource: (s) => s.panelSource,
  topics: (s) => s.topics,
  isStreaming: (s) => s.isStreaming,
  error: (s) => s.error,
  testMode: (s) => s.testMode,
  activeTopic: (s) => s.activeTopic,
  cancelledInputText: (s) => s.cancelledInputText,
  startRecordingRequestId: (s) => s.startRecordingRequestId,
  lastBackendChatTopic: (s) => s.lastBackendChatTopic,
  failedRecommendationSend: (s) => s.failedRecommendationSend,
  activeRecommendation: (s) => s.activeRecommendation,
};

const mutations = {
  openPanel: (state, payload = null) => {
    const source = payload && typeof payload === 'object' ? payload.source : payload;
    state.panelOpen = true;
    state.panelSource = source;
    if (payload?.startRecording) state.startRecordingRequestId += 1;
  },
  closePanel: (state) => { state.panelOpen = false; },
  applyBackendChatTopic: (state, rawTopic) => {
    const topic = buildBackendTopic(rawTopic);
    const topics = [...state.topics];
    const lastTopic = topics[topics.length - 1];
    if (String(lastTopic?.title || '').trim() === String(topic.title || '').trim()) return;

    state.activeTopic = { ...(state.activeTopic || {}), ...topic };
    state.lastBackendChatTopic = {
      title: topic.title,
      entityType: topic.entity?.type || null,
      signal: (state.lastBackendChatTopic?.signal || 0) + 1,
    };
    if (!lastTopic) {
      state.topics = [{ title: topic.title, messages: [] }];
      return;
    }

    if (!lastTopic.title) {
      topics[topics.length - 1] = { ...lastTopic, title: topic.title };
      state.topics = topics;
      return;
    }

    const { remainingMessages, exchangeMessages } = extractLatestExchange(lastTopic.messages);
    if (remainingMessages.length) {
      topics[topics.length - 1] = { ...lastTopic, messages: remainingMessages };
    } else {
      topics.pop();
    }
    topics.push({ title: topic.title, messages: exchangeMessages });
    state.topics = topics;
  },
  // Injects a recommendation card as a finalized (non-streaming) AI message in a
  // fresh titled topic. Used when a Pulse Item is opened: the card is rendered
  // from the item payload with no backend call.
  //
  // The card starts as a `draft` — until the user actively engages with it (chat
  // or a card CTA), opening another recommendation runs over it: the stale draft
  // topic is replaced instead of stacking up. Only non-draft recommendations may
  // later continue to the backend (aiagents).
  appendRecommendation: (state, { title = '', recommendation, bodyMarkdown = null } = {}) => {
    if (!recommendation) return;
    const message = {
      ...stampRecommendationFields(buildLocalAiMessage(bodyMarkdown || ''), recommendation),
      ...(bodyMarkdown && { isRecommendationBody: true }),
      entities: [{ ...recommendation, draft: true }],
    };
    const topic = { title: title || '', messages: [message] };
    const topics = [...state.topics];
    // Replace a trailing un-engaged draft recommendation rather than stacking.
    if (topicHasDraftRecommendation(topics[topics.length - 1])) topics.pop();
    state.topics = [...topics, topic];
    state.activeRecommendation = {
      title: title || '',
      recommendationUid: recommendation.recommendation_uid || null,
      actionUid: recommendation.uid || null,
      action: recommendation.action || null,
    };
  },
  setActiveRecommendation: (state, payload = null) => { state.activeRecommendation = payload; },
  // Promotes a recommendation out of `draft` once the user engages with it
  // (a card CTA passes its `uid`; a chat message promotes the last topic's draft).
  promoteRecommendation: (state, { uid } = {}) => {
    state.topics = promoteRecommendationEntities(state.topics, { uid });
  },
  markRecommendationSent: (state, { uid } = {}) => {
    state.topics = flagRecommendationEntities(state.topics, { uid, key: 'sent' });
  },
  markRecommendationUnsent: (state, { uid } = {}) => {
    state.topics = flagRecommendationEntities(state.topics, { uid, key: 'sent', value: false });
  },
  markRecommendationDismissed: (state, { uid } = {}) => {
    state.topics = flagRecommendationEntities(state.topics, { uid, key: 'dismissed' });
  },
  markRecommendationCompleted: (state, { uid } = {}) => {
    state.topics = flagRecommendationEntities(state.topics, { uid, key: 'completed' });
  },
  // The send whose resolve failed (`{ uid, clientUid }`), for the error alert's Retry.
  setFailedRecommendationSend: (state, payload = null) => {
    state.failedRecommendationSend = payload;
  },
  appendMessage: (state, message) => {
    if (message?.role === 'topic-entity') {
      state.topics = [...state.topics, { title: '', messages: [message] }];
      return;
    }
    if (!state.topics.length) {
      state.topics = [{ title: '', messages: [message] }];
      return;
    }
    const topics = [...state.topics];
    const lastTopic = { ...topics[topics.length - 1] };
    lastTopic.messages = [...lastTopic.messages, message];
    topics[topics.length - 1] = lastTopic;
    state.topics = topics;
  },
  setStreaming: (state, value) => { state.isStreaming = value; },
  setError: (state, message) => {
    state.error = message;
    if (!state.topics.length) return;
    const topics = [...state.topics];
    const lastTopic = { ...topics[topics.length - 1] };
    const messages = [...lastTopic.messages];
    const lastMsg = messages[messages.length - 1];
    const errorMsg = { role: 'error', text: message, isStreaming: false };
    if (lastMsg?.role === 'ai' && lastMsg.isStreaming) {
      messages[messages.length - 1] = errorMsg;
    } else {
      messages.push(errorMsg);
    }
    lastTopic.messages = messages;
    topics[topics.length - 1] = lastTopic;
    state.topics = topics;
  },
  // Also drops the failed-send stash — see the recommendation README, "Executed send".
  clearError: (state) => {
    state.error = null;
    state.failedRecommendationSend = null;
  },
  appendReasoningDelta: (state, delta) => {
    state.topics = withLastMessage(state, (last) => {
      const segs = [...last.reasoningSegments];
      const lastSeg = segs[segs.length - 1];
      if (lastSeg && lastSeg.type === 'text') {
        segs[segs.length - 1] = { ...lastSeg, text: lastSeg.text + delta };
      } else {
        segs.push({ type: 'text', text: delta });
      }
      return { ...last, reasoningSegments: segs, isReasoning: true };
    });
  },
  appendStatus: (state, statusText) => {
    const trimmed = (statusText && String(statusText).trim()) || '';
    state.topics = withLastMessage(state, (last) => ({ ...last, streamStatus: trimmed || null }));
  },
  appendTextDelta: (state, delta) => {
    state.topics = withLastMessage(state, (last) => {
      const rawText = last.rawText + delta;
      const { text, afterText, suggestions, entities, interactions, parts } = processRawText(rawText);
      return {
        ...last,
        rawText,
        text,
        afterText,
        suggestions,
        entities,
        interactions,
        parts,
        isReasoning: false,
        streamStatus: null,
      };
    });
    // Sync activeTopic when BE emits a new_topic interaction inline in the stream
    const lastTopic = state.topics[state.topics.length - 1];
    const lastMsg = lastTopic?.messages[lastTopic.messages.length - 1];
    const beTopicTag = lastMsg?.interactions?.find((i) => i.type === 'new_topic');
    if (beTopicTag) {
      state.activeTopic = {
        id: beTopicTag.primary_entity_uid || `be-${Date.now()}`,
        title: beTopicTag.title || '',
        trigger: 'backend_detected',
        toolTip: beTopicTag.tool_tip || null,
        primary_entity: beTopicTag.primary_entity_type ? {
          type: beTopicTag.primary_entity_type,
          uid: beTopicTag.primary_entity_uid || null,
        } : null,
      };
    }
  },
  finalizeLastMessage: (state) => {
    state.topics = withLastMessage(state, (last) => ({
      ...last,
      isStreaming: false,
      isWaiting: false,
      isReasoning: false,
      streamStatus: null,
    }));
  },
  markRadioSelection: (state, { messagePath, groupKey, selectedEntity } = {}) => {
    state.topics = markSelectedRadioEntity(state.topics, { messagePath, groupKey, selectedEntity });
  },
  markLastErrorRetried: (state) => {
    state.topics = withLastMessage(state, (last) => (
      last.role === 'error' ? { ...last, retried: true } : last
    ));
  },
  markFirstEventReceived: (state) => {
    if (!state.topics.length) return;
    const topics = [...state.topics];
    const lastTopic = { ...topics[topics.length - 1] };
    const messages = [...lastTopic.messages];
    const last = messages[messages.length - 1];
    if (!last || !last.isWaiting) return;
    messages[messages.length - 1] = { ...last, isWaiting: false };
    lastTopic.messages = messages;
    topics[topics.length - 1] = lastTopic;
    state.topics = topics;
  },
  setResult: (state, result) => {
    state.conversationId = result.conversation_id || state.conversationId;
    if (result.topic) {
      state.activeTopic = {
        ...(state.activeTopic || {}),
        ...result.topic,
        toolTip: result.topic.tool_tip || state.activeTopic?.toolTip || null,
      };
    }
  },
  setConversationId: (state, id) => {
    if (id) state.conversationId = id;
  },
  setTestMode: (state, value) => { state.testMode = !!value; },
  markConfirmActioned: (state, conversationId) => {
    state.topics = state.topics.map((topic) => ({
      ...topic,
      messages: topic.messages.map((msg) => (
        msg.role === 'confirm' && msg.conversationId === conversationId
          ? { ...msg, isActioned: true }
          : msg
      )),
    }));
  },
  resetConversation: (state) => {
    state.topics = [];
    state.conversationId = null;
    state.isStreaming = false;
    state.error = null;
    state.activeTopic = null;
    state.cancelledInputText = null;
    state.lastBackendChatTopic = null;
    state.failedRecommendationSend = null;
    state.activeRecommendation = null;
  },
  cancelStreaming: (state) => {
    const topics = [...state.topics];
    const lastTopic = topics[topics.length - 1];
    if (!lastTopic?.messages?.length) {
      state.isStreaming = false;
      state.cancelledInputText = null;
      return;
    }

    const nextTopic = { ...lastTopic };
    const messages = [...nextTopic.messages];
    const lastMsg = messages[messages.length - 1];
    const userMsg = messages[messages.length - 2];

    if (!lastMsg || lastMsg.role !== 'ai') {
      state.isStreaming = false;
      return;
    }

    state.cancelledInputText = userMsg?.text || null;

    if (lastMsg.text) {
      messages[messages.length - 1] = {
        ...lastMsg,
        isStreaming: false,
        isWaiting: false,
        isReasoning: false,
        streamStatus: null,
        cancelled: true,
      };
      nextTopic.messages = messages;
      topics[topics.length - 1] = nextTopic;
    } else {
      messages.splice(messages.length - 2, 2);
      if (messages.length === 0) {
        topics.splice(topics.length - 1, 1);
      } else {
        nextTopic.messages = messages;
        topics[topics.length - 1] = nextTopic;
      }
    }

    state.topics = topics;
    state.isStreaming = false;
  },
  stopStreamingSilently: (state) => {
    state.cancelledInputText = null;
    const lastTopic = state.topics[state.topics.length - 1];
    if (!lastTopic?.messages?.length) {
      state.isStreaming = false;
      return;
    }
    state.topics = withLastMessage(state, (last) => {
      if (last.role !== 'ai') return last;
      return {
        ...last,
        isStreaming: false,
        isWaiting: false,
        isReasoning: false,
        streamStatus: null,
        cancelled: false,
      };
    });
    state.isStreaming = false;
  },
  clearCancelledInputText: (state) => { state.cancelledInputText = null; },
};

/**
 * Replay canned text chunks through the normal stream handler, so the parser
 * and UI exercise exactly the same code path as a real SSE reply. Used only
 * when test-mode is active.
 *
 * We also `console.debug` every event we dispatch and the final parsed state,
 * so you can follow the "stream" and the post-parse Vuex state in DevTools.
 * Filter by `[AiChat test-mode]` in the Console to see the whole flow.
 */
const simulateStream = (commit, chunks, { label, getFinalMessage }) => new Promise((resolve) => {
  const onEvent = handleChatStreamEvent(commit);
  const tag = `[AiChat test-mode] ${label}`;
  const rawReply = chunks.join('');
  debugLogger.info(
    `%c${tag}%c raw LLM-like reply:\n%c${rawReply}`,
    'background:#4a90e2;color:#fff;padding:2px 6px;border-radius:3px;font-weight:bold',
    'color:inherit',
    'color:#888;white-space:pre-wrap',
  );
  debugLogger.groupCollapsed(`${tag} (per-chunk detail)`);
  debugLogger.debug(`${chunks.length} chunk(s), ${TEST_MODE_CHUNK_DELAY_MS}ms apart`);
  let i = 0;
  const next = () => {
    if (i >= chunks.length) {
      try {
        const finalMsg = getFinalMessage?.();
        if (finalMsg) {
          debugLogger.debug('final message state:', {
            text: finalMsg.text,
            entities: finalMsg.entities,
            interactions: finalMsg.interactions,
            suggestions: finalMsg.suggestions,
          });
        }
      } finally {
        debugLogger.groupEnd();
      }
      resolve();
      return;
    }
    const chunk = chunks[i];
    debugLogger.debug(`chunk ${i + 1}/${chunks.length} →`, chunk);
    onEvent({ type: 'text_delta', text: chunk });
    i += 1;
    setTimeout(next, TEST_MODE_CHUNK_DELAY_MS);
  };
  next();
});

const actions = {
  openPanel: ({ commit }, source) => commit('openPanel', source),
  closePanel: ({ commit }) => commit('closePanel'),
  markRadioSelection: ({ commit }, payload) => commit('markRadioSelection', payload),
  markLastErrorRetried: ({ commit }) => commit('markLastErrorRetried'),
  resetConversation: ({ commit, state: s }) => {
    if (s.isStreaming) { currentStreamController?.abort(); if (s.conversationId) aiChatService.abortStream(s.conversationId); }
    commit('resetConversation');
  },
  toggleTestMode: ({ commit, state: s }) => commit('setTestMode', !s.testMode),
  // Opens Conversita and renders a Pulse Item's recommendation card locally,
  // straight from its payload. Deliberately does NOT contact aiagents — the
  // closed-artifact presentation is payload-driven (no LLM) per VCITA2-14490.
  openRecommendation({ commit, dispatch, state: s, rootGetters }, launchRequest = {}) {
    const { title, recommendation } = launchRequest;
    if (!recommendation) return;
    const bodyMarkdown = rootGetters['BusinessStore/hasFeature'](RECOMMENDATION_BODY_MARKDOWN_FLAG)
      ? recommendation.display?.body_markdown || null
      : null;
    // Abort any in-flight stream first so late deltas can't land inside the
    // recommendation card. restoreInput:false silently drops the half-finished
    // answer rather than dumping its text back into the input box.
    if (s.isStreaming) dispatch('stopStreaming', { restoreInput: false });
    commit('openPanel', { source: 'pulse' });
    commit('appendRecommendation', { title, recommendation, bodyMarkdown });
  },
  // See the recommendation README, "Deterministic tile CTA".
  async openRecommendationSend({ commit, dispatch }, launchRequest = {}) {
    const { recommendation } = launchRequest;
    if (!recommendation) return;
    await dispatch('openRecommendation', launchRequest);
    commit('promoteRecommendation', { uid: recommendation.uid });
    commit('markRecommendationSent', { uid: recommendation.uid });
  },
  revertRecommendationSend: ({ commit }, { uid } = {}) => {
    if (uid) commit('markRecommendationUnsent', { uid });
  },
  // The Pulse tile's one-click entry point for a `chat_with_bizai` item (see README).
  async openRecommendationWorkflow({ dispatch }, launchRequest = {}) {
    const { recommendation } = launchRequest;
    if (!recommendation) return;
    await dispatch('openRecommendation', launchRequest);
    await dispatch('promoteRecommendation', { uid: recommendation.uid });
    await dispatch('chatAboutRecommendation', {
      uid: recommendation.uid,
      recommendationUid: recommendation.recommendation_uid,
      userText: recommendation.artifact?.cta_label || '',
      ctaLabel: recommendation.artifact?.cta_label || '',
    });
  },
  // Marks a recommendation as engaged (non-draft) so it is preserved and eligible
  // to continue to aiagents. Fired from a card CTA (with the card `uid`).
  promoteRecommendation: ({ commit }, payload = {}) => commit('promoteRecommendation', payload),
  // Executes the recommendation's action and reflects it in the transcript client-side.
  // The sequence and its rationale live in entities/recommendation/README.md
  // ("Executed send"); a retry passes no `userText`, its echo already being in place.
  async executeRecommendation({ commit, dispatch, state: s }, {
    uid, clientUid, userText, recommendationUid, action, trigger = MESSAGE_TRIGGERS.RECOMMENDATION,
  } = {}) {
    if (!uid) {
      console.error('Cannot resolve recommendation action: no action uid', recommendationUid);
      commit('setError', i18n.t('common.errors.general_error'));
      return;
    }
    commit('clearError');
    trackMessageSent({ trigger });
    if (userText) commit('appendMessage', buildUserMessage(userText));
    let result = null;
    try {
      result = extractResolveResult(await bizAiService.resolveRecommendationAction(uid));
    } catch {
      commit('setFailedRecommendationSend', { uid, clientUid, recommendationUid, action });
      commit('setError', i18n.t('common.errors.general_error'));
      return;
    }
    commit('setFailedRecommendationSend', null);
    commit('markRecommendationSent', { uid });
    commit('markRecommendationCompleted', { uid });
    // Skipped when the user asked something else while the resolve was in flight: every
    // stream update rewrites the *last* message, so appending here would hand the
    // confirmation to the incoming reply. The card's sent state carries the feedback.
    if (!s.isStreaming) {
      commit('appendMessage', buildLocalAiMessage(i18n.t('ai_chat.entities.recommendation.executed_confirmation')));
    }
    await dispatch('appendExecutedRecommendationNote', {
      actionUid: uid, clientUid, recommendationUid, action, result,
    });
  },
  /** Tells the assistant the action was executed — a silent write; see README. */
  async appendExecutedRecommendationNote({ dispatch }, {
    actionUid, clientUid, recommendationUid, action, result,
  } = {}) {
    if (!actionUid) return;
    await dispatch('sendMessage', {
      text: buildExecutedRecommendationMessage({ actionUid, clientUid }),
      suppressUserMessage: true,
      appendOnly: true,
      topic: buildExecutedRecommendationTopic({ recommendationUid, actionUid, action, result }),
    });
  },
  /**
   * The non-deterministic counterpart of `executeRecommendation` — streams a workflow rather
   * than resolving. See the recommendation README, "Chat with BizAI", for the contract, the
   * ordering invariants and what this deliberately omits.
   */
  async chatAboutRecommendation({ commit, dispatch, state: s }, {
    uid, recommendationUid, userText, ctaLabel, trigger = MESSAGE_TRIGGERS.RECOMMENDATION,
  } = {}) {
    if (!recommendationUid) {
      console.error('Cannot start a recommendation workflow: no recommendation uid', uid);
      return;
    }
    commit('clearError');
    if (userText) commit('appendMessage', buildUserMessage(userText));
    await dispatch('sendMessage', {
      text: ctaLabel,
      suppressUserMessage: true,
      workflow: buildRecommendationWorkflow({ recommendationUid, actionUid: uid }),
      tracking: { trigger },
    });
    // `sendMessage` swallows failures into `setError`, so the outcome is read off state.
    if (s.error) {
      commit('setFailedRecommendationSend', { uid, recommendationUid, ctaLabel, kind: 'chat_workflow' });
      return;
    }
    commit('markRecommendationSent', { uid });
  },
  // `uid` is the **recommendation** uid, `cardUid` the action uid the card is keyed by.
  // A backend-streamed `<card>` carries no recommendation uid yet, so there is nothing to
  // dismiss — logged rather than swallowed (see README, "Not relevant").
  async dismissRecommendation({ commit }, { uid, cardUid } = {}) {
    if (!uid) {
      console.error('Cannot dismiss recommendation: no recommendation uid', cardUid);
      commit('setError', i18n.t('common.errors.general_error'));
      commit('markLastErrorRetried');
      return;
    }
    commit('clearError');
    try {
      await bizAiService.updateRecommendation(uid, 'dismissed');
    } catch {
      commit('setError', i18n.t('common.errors.general_error'));
      commit('markLastErrorRetried');
      return;
    }
    commit('markRecommendationDismissed', { uid: cardUid });
  },
  // Same uid split as the dismiss above: the write is keyed on the recommendation, the
  // card's flag on its action uid.
  async completeRecommendation({ commit }, { uid, cardUid } = {}) {
    if (!uid) {
      console.error('Cannot complete recommendation: no recommendation uid', cardUid);
      commit('setError', i18n.t('common.errors.general_error'));
      commit('markLastErrorRetried');
      return;
    }
    commit('clearError');
    try {
      await bizAiService.updateRecommendation(uid, 'completed');
    } catch {
      commit('setError', i18n.t('common.errors.general_error'));
      commit('markLastErrorRetried');
      return;
    }
    commit('markRecommendationCompleted', { uid: cardUid });
  },
  async launchWorkflow({ dispatch, commit }, launchRequest = {}) {
    const text = String(launchRequest.message || '').trim();
    if (!text) return;

    if (launchRequest.clearConversation) await dispatch('resetConversation');
    commit('openPanel', launchRequest.panelSource);
    const workflow = launchRequest.workflow && typeof launchRequest.workflow === 'object'
      ? launchRequest.workflow
      : null;

    await dispatch('sendMessage', {
      text,
      context: launchRequest.context || null,
      workflow,
      // Workflow launch messages are app-generated copy — safe to render as markdown.
      isMarkdown: true,
      tracking: { trigger: MESSAGE_TRIGGERS.WORKFLOW_LAUNCH },
    });
  },
  stopStreaming: ({ commit, state: s }, { restoreInput = true } = {}) => {
    const controller = currentStreamController;
    currentStreamController = null;
    currentStreamRequestId += 1;
    controller?.abort();
    if (s.conversationId) aiChatService.abortStream(s.conversationId);
    if (s.isStreaming) commit(restoreInput ? 'cancelStreaming' : 'stopStreamingSilently');
  },
  clearCancelledInputText: ({ commit }) => commit('clearCancelledInputText'),

  async sendMessage({ commit, state: s }, messageOptions = {}) {
    const {
      text,
      context,
      topic,
      workflow,
      selectedEntities,
      topicEntity,
      isMarkdown,
      suppressUserMessage = false,
      appendOnly = false,
      tracking,
    } = messageOptions;
    commit('clearError');
    if (!appendOnly) trackMessageSent(tracking);
    const messageText = String(text || '').trim();
    // `suppressUserMessage` marks app-generated text: sent to aiagents, never rendered
    // as the user's own message. `appendOnly` means the backend records it and does not
    // answer, so there is no stream to show either.
    const isUserText = Boolean(messageText) && !suppressUserMessage;
    const showStreamUi = Boolean(messageText) && !appendOnly;
    // Typing a message actively engages the current recommendation (if any),
    // promoting it out of draft so a later open won't run over it.
    if (isUserText) commit('promoteRecommendation');
    if (topicEntity) {
      // Pinning another entity makes it the subject, not the card (see the README).
      commit('setActiveRecommendation', null);
      commit('appendMessage', { role: 'topic-entity', entity: topicEntity, isStreaming: false });
    } else if (isUserText) {
      commit('appendMessage', buildUserMessage(messageText, { isMarkdown: Boolean(isMarkdown) }));
    }
    if (showStreamUi) {
      commit('appendMessage', buildAiMessage());
      commit('setStreaming', true);
    }

    const fixture = s.testMode ? findFixtureByUserText(messageText) : null;
    if (fixture) {
      if (fixture.isError) {
        commit('setError', 'Something went wrong. Please try again.');
        commit('finalizeLastMessage');
        commit('setStreaming', false);
        return;
      }
      try {
        await simulateStream(commit, fixture.chunks, {
          label: `send "${messageText}"`,
          getFinalMessage: () => lastTopicLastMessage(s.topics),
        });
      } finally {
        commit('finalizeLastMessage');
        commit('setStreaming', false);
      }
      return;
    }

    const payload = { viewed_page: context };
    if (messageText) payload.message = messageText;
    if (s.conversationId) payload.conversation_id = s.conversationId;
    if (selectedEntities?.length) {
      payload.interaction = { entities: selectedEntities.map(buildSelectedEntityPayload) };
    }
    // Only real user text inherits the open card — see the recommendation README.
    const chatTopic = topic
      || (isUserText ? buildRecommendationTopic(s.activeRecommendation || {}) : null);
    if (chatTopic) {
      payload.chat_topic = chatTopic;
    }
    if (workflow && typeof workflow === 'object') {
      payload.chat_workflow = workflow;
    }
    if (appendOnly) payload.append_only = true;

    // An append-only note renders nothing and can't be cancelled, so it must stay out of
    // the cancellation slot: taking it over would strand a user stream still in flight,
    // whose request id would no longer match and could therefore never finalize.
    const controller = new AbortController();
    let streamRequestId = currentStreamRequestId;
    if (!appendOnly) {
      streamRequestId = currentStreamRequestId + 1;
      currentStreamRequestId = streamRequestId;
      currentStreamController = controller;
    }
    const { signal } = controller;
    let aborted = false;

    try {
      await aiChatService.streamMessage(payload, handleChatStreamEvent(commit, { showStreamUi }), signal);
    } catch (err) {
      if (err.name === 'AbortError') {
        aborted = true;
        if (showStreamUi && currentStreamRequestId === streamRequestId) commit('cancelStreaming');
      } else if (showStreamUi) {
        commit('setError', 'Something went wrong. Please try again.');
      }
    } finally {
      // Identity, not the request id: an append-only note never claimed the slot, so it
      // must not release someone else's controller.
      if (currentStreamController === controller) currentStreamController = null;
      if (showStreamUi && !aborted && currentStreamRequestId === streamRequestId) {
        commit('finalizeLastMessage');
        commit('setStreaming', false);
      }
    }
  },

  /**
   * Reply to a HITL interaction streamed by the assistant.
   *
   * Accepts `{ uid?, confirm }`. Sends `{ interaction: { uid?, confirm }, conversation_id }`.
   * `uid` is present for code-driven (parked) slots; absent for LLM-driven confirms.
   * `confirm` is a string: "yes" executes, anything else is treated as a cancel reason.
   */
  async confirmAction({ commit, state: s }, payload) {
    const { uid, confirm, userText } = payload || {};
    commit('clearError');
    if (!s.conversationId) {
      commit('setError', 'Could not process your confirmation. Wait for the reply to finish, then try again.');
      return;
    }
    if (!confirm) {
      commit('setError', 'Could not process your confirmation. Please try again.');
      return;
    }
    const showStreamUi = Boolean(userText);
    if (userText) commit('appendMessage', buildUserMessage(userText));
    if (showStreamUi) {
      commit('appendMessage', buildAiMessage());
      commit('setStreaming', true);
    }

    const replyChunks = s.testMode ? findReplyChunks(null, confirm) : null;
    if (replyChunks) {
      try {
        await simulateStream(commit, replyChunks, {
          label: `confirm → ${confirm}`,
          getFinalMessage: () => lastTopicLastMessage(s.topics),
        });
      } finally {
        commit('finalizeLastMessage');
        commit('setStreaming', false);
      }
      return;
    }

    const interaction = { confirm };
    if (uid) interaction.uid = uid;

    const streamRequestId = currentStreamRequestId + 1;
    currentStreamRequestId = streamRequestId;
    currentStreamController = new AbortController();
    const { signal } = currentStreamController;
    let aborted = false;

    try {
      await aiChatService.streamMessage(
        { conversation_id: s.conversationId, interaction },
        handleChatStreamEvent(commit, { showStreamUi }),
        signal,
      );
    } catch (err) {
      if (err.name === 'AbortError') {
        aborted = true;
        if (showStreamUi && currentStreamRequestId === streamRequestId) commit('cancelStreaming');
      } else if (showStreamUi) {
        commit('setError', 'Something went wrong. Please try again.');
      }
    } finally {
      if (currentStreamRequestId === streamRequestId) currentStreamController = null;
      if (showStreamUi && !aborted && currentStreamRequestId === streamRequestId) {
        commit('finalizeLastMessage');
        commit('setStreaming', false);
      }
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
